import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ChevronLeftLarge from "@atlaskit/icon/glyph/chevron-left-large";
import { token } from "@atlaskit/tokens";

import { WACAvatar } from "@/components/global-nav/user-profile-new/src/components/avatar";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    LogoV2 as Logo,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import { CloseIcon } from "./icons/close";
import { MenuIcon } from "./icons/menu";
import { DropdownType } from "./mobile-nav";

const moreLogoCss = css({
    display: "flex",
    alignItems: "center"
});

const logoCss = css({
    height: "21px"
});

const backButtonCss = css({
    color: token("color.text.selected"),
    display: "flex",
    alignItems: "center"
});

const chevronCss = css({
    marginBlock: "6px 0"
});

const profileAndMenuButtonCss = css({
    display: "flex",
    gap: token("space.200"),
    alignContent: "center"
});

const iconContainerCss = css({
    height: "40px",
    display: "flex",
    alignItems: "center"
});

const profileCss = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

type MobileNavTopProps = {
    productKey: ProductKeyTypes;
    isLoggedIn: boolean;
    userName: string | undefined;
    picture: string | undefined;
    showMenu: boolean;
    handleToggleMenu: (showMenu: boolean) => void;
    menuPage: DropdownType;
    handleMenuPage: (menuPage: DropdownType) => void;
};

export const MobileNavTop = ({
    productKey,
    isLoggedIn,
    userName,
    picture,
    showMenu,
    handleToggleMenu,
    menuPage,
    handleMenuPage
}: MobileNavTopProps) => {
    const intl = useIntl();
    const hrefAtlassianLogo = useLocalizedHref("/");

    return (
        <>
            <div css={moreLogoCss}>
                {menuPage === "default" || !showMenu ? (
                    <a
                        css={logoCss}
                        href={hrefAtlassianLogo}
                        aria-label={intl.formatMessage({
                            id: "globalnavv2.logo-label",
                            defaultMessage: "Atlassian logo"
                        })}
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container="globalNavWacV2"
                        data-product-key={productKey}
                    >
                        <Logo
                            brand="atlassian"
                            type="logo"
                            appearance="brand"
                            height={20}
                            width={156}
                        />
                    </a>
                ) : (
                    <button
                        style={{ all: "unset", width: "100%" }}
                        onClick={(e) => {
                            e.preventDefault();
                            handleMenuPage("default");
                        }}
                    >
                        <div css={backButtonCss}>
                            <div css={chevronCss}>
                                <ChevronLeftLarge label="" />
                            </div>
                            <span>
                                {intl.formatMessage({
                                    id: "globalnavv2.menu-back",
                                    defaultMessage: "Back"
                                })}
                            </span>
                        </div>
                    </button>
                )}
            </div>
            <div css={profileAndMenuButtonCss}>
                {isLoggedIn && userName && picture && (
                    <div css={profileCss}>
                        <WACAvatar
                            name={userName}
                            src={picture}
                            size={"medium"}
                        />
                    </div>
                )}
                <div css={iconContainerCss}>
                    <button
                        style={{ all: "unset" }}
                        aria-label={intl.formatMessage({
                            id: "globalnavv2.menu-icon.toggle",
                            defaultMessage: "Toggle Menu"
                        })}
                        onClick={(e) => {
                            e.preventDefault();
                            handleToggleMenu(!showMenu);
                            handleMenuPage("default");
                        }}
                    >
                        {showMenu ? (
                            <CloseIcon
                                label={intl.formatMessage({
                                    id: "globalnavv2.menu-icon.close",
                                    defaultMessage: "Close Menu"
                                })}
                            />
                        ) : (
                            <MenuIcon
                                label={intl.formatMessage({
                                    id: "globalnavv2.menu-icon.open",
                                    defaultMessage: "Open Menu"
                                })}
                            />
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};
